import { useEffect, useState } from 'react';
import type { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

import { Heading } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Loader } from '@cognite/cogs-core-v11/components/loader/loader.tsx';
import { Modal } from '@cognite/cogs-core-v11/components/modal/modal.tsx';
import type { AuthStateUser } from '@cognite/e2e-auth';

import { USER_INSTANCE_SPACE } from '../../constants/user-data-model';
import { useUserSpaceAccess } from '../../features/access/hooks/use-user-space-rw-access';
import { AppConfigPage } from '../../features/app-config/app-config';
import { AppConfigErrorScreen } from '../../features/app-config/app-config-error-screen/app-config-error-screen';
import { useDisciplines } from '../../features/app-config/hooks/use-configured-disciplines';
import { useLocations } from '../../features/app-config/hooks/use-locations';
import { useIsAppConfiguratorQuery } from '../../features/app-config/hooks/use-query/use-is-app-configurator-query';
import { selectedRootLocationAtom } from '../../features/app-config/state/state';
import { useInfieldLanguage } from '../../features/i18n/use-infield-language';
import { UserOnboarding } from '../../features/user/user-onboarding/user-onboarding';
import { useInfieldLogout } from '../../hooks/use-infield-logout';
import { PageContainerStyle } from '../../pages/elements';

import { MissingPermissionsErrorComponent } from './components/missing-permissions-error-component';
import { validateUserLocation } from './utils/validateUserLocation';

interface Props {
  mocks?: AuthStateUser;
}

export const UserOnboardingProvider: FC<PropsWithChildren<Props>> = ({
  children,
  mocks,
}) => {
  const { t } = useTranslation('user');
  const setSelectedRootLocation = useSetRecoilState(selectedRootLocationAtom);
  const { logout } = useInfieldLogout();
  const [showUserOnboarding, setShowUserOnboarding] = useState(false);
  const { data: hasAppConfigPermission } = useIsAppConfiguratorQuery();
  const [showAppConfig, setShowAppConfig] = useState(false);

  const userSpace = USER_INSTANCE_SPACE;
  const {
    selectableLocations,
    isSelectableLocationsLoading,
    configuredRootLocationOptions,
    selectedRootLocation,
    handleSaveLocation,
    updateLocationInLocalStorage,
  } = useLocations();

  const {
    data: hasUserSpaceAccess,
    isSuccess: isUserSpaceAccessSuccess,
    isLoading: isLoadingUserSpaceAccess,
  } = useUserSpaceAccess();

  const { selectedLanguage, supportedLanguages, updateLanguage } =
    useInfieldLanguage();

  const {
    configuredDisciplineOptions,
    selectedDiscipline,
    handleSaveDiscipline,
  } = useDisciplines();

  const validUserLocation = validateUserLocation(
    selectableLocations,
    selectedRootLocation?.value
  );

  const isLoadingUserData =
    (isSelectableLocationsLoading || isLoadingUserSpaceAccess) && !mocks;

  useEffect(() => {
    if (!validUserLocation && !isLoadingUserData) {
      setShowUserOnboarding(true);
    } else if (validUserLocation) {
      if (selectedRootLocation?.value) {
        updateLocationInLocalStorage(selectedRootLocation?.value);
      }
      setSelectedRootLocation(selectedRootLocation?.value);
    }
  }, [
    validUserLocation,
    isLoadingUserData,
    setSelectedRootLocation,
    selectedRootLocation,
    updateLocationInLocalStorage,
  ]);

  const handleSaveUserOnboarding = () => {
    setShowUserOnboarding(false);
  };

  if (mocks) {
    return <>{children}</>;
  }

  if (!hasUserSpaceAccess && isUserSpaceAccessSuccess) {
    return (
      <>
        <MissingPermissionsErrorComponent
          userSpace={userSpace}
          hasAppConfigPermission={hasAppConfigPermission}
          onShowAppConfig={() => setShowAppConfig(true)}
          onLogout={logout}
        />
        <Modal
          title="App configuration"
          visible={showAppConfig}
          size="full-screen"
          onCancel={() => setShowAppConfig(false)}
          onOk={() => setShowAppConfig(false)}
        >
          <AppConfigPage hasAppConfigPermission={hasAppConfigPermission} />
        </Modal>
      </>
    );
  }

  if (isLoadingUserData && !showUserOnboarding) {
    return (
      <Loader
        infoText={
          <Heading level={6}>
            {t('loadingUserData', 'Loading user data')}
          </Heading>
        }
      />
    );
  }

  if (
    !configuredRootLocationOptions ||
    configuredRootLocationOptions.length === 0
  ) {
    return (
      <AppConfigErrorScreen
        title={t(
          'USER_ERROR_LOCATION_MISSING_TITLE',
          'Error: Location is missing.'
        )}
        body={t(
          'USER_ERROR_LOCATION_MISSING_DESCRIPTION',
          "The InField admin has not defined any location yet, or you don't have access to any of the configured locations. Contact your admin if you have any questions."
        )}
      />
    );
  }

  if (showUserOnboarding) {
    return (
      <PageContainerStyle>
        <UserOnboarding
          languages={supportedLanguages}
          selectedLanguage={selectedLanguage}
          onSelectLanguage={updateLanguage}
          locations={configuredRootLocationOptions}
          selectedLocation={selectedRootLocation}
          onSelectLocation={handleSaveLocation}
          disciplines={configuredDisciplineOptions}
          selectedDiscipline={selectedDiscipline}
          onSelectDiscipline={handleSaveDiscipline}
          onComplete={handleSaveUserOnboarding}
        />
      </PageContainerStyle>
    );
  }

  return <>{children}</>;
};
